import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchInvoices, fetchQuotes } from '../models/InvoiceService';
import {
  toDate, invoiceTotal, isPaid, basisDate,
  computePeriod, currentQuarter, openDeclaration, shiftQuarter,
  daysUntil, fmtDay, fmtDayShort,
} from '../models/urssaf';
import PageShell, { Btn, Pill, fmtEUR } from '../components/design/PageShell';

const eur0 = (n) => Math.round(n).toLocaleString('fr-FR');

const Home = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [invs, qts] = await Promise.all([fetchInvoices(), fetchQuotes()]);
        setInvoices(invs);
        setQuotes(qts);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const now = new Date();
  const current = currentQuarter(now);
  const toDeclare = openDeclaration(now);

  const currentPeriod = useMemo(
    () => computePeriod(invoices, current.start, current.end),
    [invoices, current]
  );
  const declarePeriod = useMemo(
    () => (toDeclare ? computePeriod(invoices, toDeclare.start, toDeclare.end) : null),
    [invoices, toDeclare]
  );

  const stats = useMemo(() => {
    const prev = shiftQuarter(current, -1);
    const prevPeriod = computePeriod(invoices, prev.start, prev.end);

    const thisQ = currentPeriod.invoices;
    const caQ = currentPeriod.revenue;
    const caPrev = prevPeriod.revenue;

    const paid = thisQ.filter(isPaid);
    const pending = thisQ.filter(i => !isPaid(i));
    const paidAmount = paid.reduce((a, i) => a + invoiceTotal(i), 0);
    const pendingAmount = pending.reduce((a, i) => a + invoiceTotal(i), 0);

    const delta = caPrev > 0 ? ((caQ - caPrev) / caPrev) * 100 : null;
    const objective = 6200;
    const progress = Math.min(100, (caQ / objective) * 100);

    const recentInvoices = [...invoices]
      .filter(i => basisDate(i))
      .sort((a, b) => basisDate(b) - basisDate(a))
      .slice(0, 4);

    return {
      caQ, caPrev, delta, progress, objective, prev,
      thisQCount: thisQ.length,
      paid: paid.length, paidAmount,
      pending: pending.length, pendingAmount,
      quotesOpen: quotes.filter(q => !q.converted).length,
      recentInvoices,
      quarter: `T${current.q}`,
      year: current.year,
    };
  }, [invoices, quotes, current, currentPeriod]);

  const formatDate = (d) => {
    const date = toDate(d);
    if (!date) return '--/--';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <PageShell
      section="accueil"
      number="01"
      title="Accueil"
      subtitle={`Aperçu de l'activité · trimestre en cours ${stats.quarter} ${stats.year}`}
      actions={<>
        <Btn kind="ghost" icon="chart" onClick={() => navigate('/charges')}>Charges</Btn>
        <Btn kind="primary" icon="plus" onClick={() => navigate('/invoices')}>Nouvelle facture</Btn>
      </>}
    >
      {/* Meta strip */}
      <section className="ta-meta-strip">
        <div>
          <div className="k">Période</div>
          <div className="v">{stats.quarter} · {stats.year}</div>
        </div>
        <div>
          <div className="k">Factures</div>
          <div className="v">{stats.thisQCount} émise{stats.thisQCount > 1 ? 's' : ''}</div>
        </div>
        <div>
          <div className="k">Payées</div>
          <div className="v">{stats.paid} / {stats.thisQCount}</div>
        </div>
        <div>
          <div className="k">Statut</div>
          <div className="v"><span style={{ color: 'var(--ok)' }}>● </span>en cours</div>
        </div>
      </section>

      {/* Charges URSSAF — déclaration ouverte + trimestre en cours */}
      <section className="ta-panel" style={{ marginBottom: 48 }}>
        <div className="ta-panel-head">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className="ta-panel-title">Charges URSSAF</span>
              {toDeclare
                ? <Pill tone="warn">{toDeclare.short} à déclarer</Pill>
                : <Pill tone="accent">aucune déclaration ouverte</Pill>}
            </div>
            <div className="ta-panel-sub">
              {toDeclare
                ? `Déclaration du ${toDeclare.label} ouverte jusqu'au ${fmtDay(toDeclare.due)}`
                : `Prochaine échéance le ${fmtDay(current.due)} pour le ${current.label}`}
            </div>
          </div>
          <Btn kind="ghost" icon="arrowR" onClick={() => navigate('/charges')}>Détail</Btn>
        </div>

        <div className="ta-figs">
          {toDeclare && declarePeriod ? (
            <>
              <div>
                <div className="k">{toDeclare.short} · CA à déclarer</div>
                <div className="v">{eur0(declarePeriod.charges.base)} €</div>
                <div className="h">{fmtDayShort(toDeclare.start)} → {fmtDayShort(toDeclare.end)}</div>
              </div>
              <div>
                <div className="k">À payer</div>
                <div className="v" style={{ color: 'var(--warn)' }}>{eur0(declarePeriod.charges.total)} €</div>
                <div className="h">
                  {(() => {
                    const d = daysUntil(toDeclare.due);
                    return d > 1 ? `${d} jours restants` : d === 1 ? 'demain' : d === 0 ? "aujourd'hui" : 'échéance dépassée';
                  })()}
                </div>
              </div>
              <div>
                <div className="k">{current.short} · en cours</div>
                <div className="v" style={{ color: 'var(--accent)' }}>{eur0(currentPeriod.charges.total)} €</div>
                <div className="h">à provisionner · CA {eur0(currentPeriod.charges.base)} €</div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="k">{current.short} · CA du trimestre</div>
                <div className="v">{eur0(currentPeriod.charges.base)} €</div>
                <div className="h">{fmtDayShort(current.start)} → {fmtDayShort(current.end)}</div>
              </div>
              <div>
                <div className="k">Charges estimées</div>
                <div className="v" style={{ color: 'var(--warn)' }}>{eur0(currentPeriod.charges.total)} €</div>
                <div className="h">à provisionner</div>
              </div>
              <div>
                <div className="k">Net après charges</div>
                <div className="v" style={{ color: 'var(--accent)' }}>{eur0(currentPeriod.charges.net)} €</div>
                <div className="h">déclaration le {fmtDayShort(current.due)}</div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Big number — CA */}
      <section className="ta-hero">
        <div className="eyebrow">{"// "}Chiffre d'affaires trimestriel</div>
        <div className="ta-hero-fig">
          <span className="ta-hero-val">{fmtEUR(stats.caQ)}</span>
          <span className="ta-hero-unit">€ HT</span>
        </div>
        <div className="ta-hero-delta">
          {stats.delta !== null && (
            <span style={{ color: stats.delta >= 0 ? 'var(--ok)' : 'var(--warn)' }}>
              {stats.delta >= 0 ? '↗' : '↘'} {stats.delta >= 0 ? '+' : ''}{stats.delta.toFixed(1)}%
            </span>
          )}
          <span style={{ color: 'var(--ink-4)' }}>
            {' '}vs {stats.prev.short} · {fmtEUR(stats.caPrev)} €
          </span>
        </div>
        <div className="ta-hero-bar">
          <div className="ta-hero-bar-fill" style={{ width: `${stats.progress}%` }} />
          <div className="ta-hero-bar-marks">
            <span>0</span>
            <span>2k</span>
            <span>4k</span>
            <span style={{ color: 'var(--accent)' }}>{fmtEUR(stats.objective / 1000)}k · objectif</span>
          </div>
        </div>
      </section>

      {/* Two-col summary */}
      <section className="ta-split">
        <div>
          <div className="eyebrow" style={{ marginBottom: 16 }}>§ 01.A · À traiter</div>
          <div className="ta-tile-list">
            <div className="ta-tile-row">
              <div>
                <div className="t">{stats.pending} facture{stats.pending > 1 ? 's' : ''} en attente</div>
                <div className="d">À relancer et encaisser</div>
              </div>
              <div className="mono tnum" style={{ color: 'var(--warn)' }}>{fmtEUR(stats.pendingAmount)} €</div>
            </div>
            <div className="ta-tile-row">
              <div>
                <div className="t">{stats.quotesOpen} devis ouvert{stats.quotesOpen > 1 ? 's' : ''}</div>
                <div className="d">À transformer ou relancer</div>
              </div>
              <div className="mono tnum" style={{ color: 'var(--ink-3)' }}>—</div>
            </div>
            <div className="ta-tile-row" style={{ cursor: 'pointer' }} onClick={() => navigate('/charges')}>
              <div>
                <div className="t">
                  {toDeclare ? `Déclaration URSSAF ${toDeclare.short}` : `Déclaration URSSAF ${current.short}`}
                </div>
                <div className="d">
                  {toDeclare
                    ? `À déclarer avant le ${fmtDay(toDeclare.due)}`
                    : `Ouverture le 1er ${new Date(current.year, current.q * 3, 1).toLocaleDateString('fr-FR', { month: 'long' })}`}
                </div>
              </div>
              <div className="mono tnum" style={{ color: toDeclare ? 'var(--warn)' : 'var(--ink-3)' }}>
                {toDeclare && declarePeriod ? `${eur0(declarePeriod.charges.total)} €` : '—'}
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="eyebrow" style={{ marginBottom: 16 }}>§ 01.B · Activité récente</div>
          <div className="ta-tile-list">
            {loading && <div className="ta-log-row"><div className="mono">...</div><div>Chargement</div></div>}
            {!loading && stats.recentInvoices.length === 0 && (
              <div className="ta-log-row"><div className="mono" /><div>Aucune activité récente</div></div>
            )}
            {stats.recentInvoices.map((inv, i) => (
              <div key={inv.id || i} className="ta-log-row">
                <div className="mono" style={{ color: 'var(--ink-4)', fontSize: 11 }}>{formatDate(inv.created)}</div>
                <div>
                  <div style={{ fontSize: 13 }}>Facture #{inv.num || '—'} émise</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                    {inv.client || '—'} · {fmtEUR(invoiceTotal(inv))} €
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Home;
